import { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import Card from "@/components/base/Card";
import Badge from "@/components/base/Badge";
import Button from "@/components/base/Button";
import Input from "@/components/base/Input";
import { type UserProfile } from "@/mocks/data";
import { documentService } from "@/services/api";

interface DocumentoInst {
  id: string;
  nombre: string;
  tipo: string;
  estado: "activo" | "procesando" | "error";
  version: string;
  tamanho: string;
  fecha: string;
  paginas: number;
  url: string;
}

function getStoredUser(): UserProfile | null {
  const stored = localStorage.getItem("convivencia_user");
  return stored ? JSON.parse(stored) : null;
}

const tipoLabels: Record<string, string> = {
  reglamento: "Reglamento",
  protocolo: "Protocolo",
  manual: "Manual",
  pei: "PEI",
  otro: "Otro",
};

export default function AdminDocumentosPage() {
  const [user] = useState<UserProfile | null>(getStoredUser);
  const [documents, setDocuments] = useState<DocumentoInst[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [uploading, setUploading] = useState(false);

  // Modal states
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [newDocName, setNewDocName] = useState("");
  const [newDocType, setNewDocType] = useState("reglamento");
  const [selectedFile, setSelectedFile] = useState<File | null>(null);

  const loadDocuments = async () => {
    setLoading(true);
    setError("");
    try {
      const data = await documentService.listInstitucionales();
      if (Array.isArray(data)) {
        setDocuments(data.map((d: any) => ({
          id: d.id,
          nombre: d.nombre,
          tipo: d.tipo,
          estado: "activo",
          version: String(d.version),
          tamanho: "Azure Blob",
          fecha: d.created_at ? new Date(d.created_at).toLocaleString("es-CL") : "—",
          paginas: 0,
          url: d.url
        })));
      } else {
        throw new Error("Invalid list format received from API");
      }
    } catch (err: any) {
      console.error("Error loading documents:", err);
      setError("Error al cargar documentos institucionales desde Azure: " + (err.response?.data?.detail || err.message));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user) {
      loadDocuments();
    }
  }, [user]);

  if (!user) return null;

  const handleOpenUpload = () => {
    setNewDocName("");
    setNewDocType("reglamento");
    setSelectedFile(null);
    setShowUploadModal(true);
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      setSelectedFile(file);
      // Pre-fill name if empty
      if (!newDocName) {
        setNewDocName(file.name.replace(/\.[^/.]+$/, ""));
      }
    }
  };

  const handleUploadSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedFile) {
      alert("Por favor seleccione un archivo para subir.");
      return;
    }
    if (!newDocName.trim()) {
      alert("Por favor ingrese el nombre del documento.");
      return;
    }

    const formData = new FormData();
    formData.append("nombre", newDocName.trim());
    formData.append("tipo", newDocType);
    formData.append("file", selectedFile);

    setUploading(true);
    try {
      await documentService.uploadInstitucional(formData);
      setShowUploadModal(false);
      setSelectedFile(null);
      await loadDocuments();
    } catch (err: any) {
      alert("Error al subir archivo a Azure Storage: " + (err.response?.data?.detail || err.message));
    } finally {
      setUploading(false);
    }
  };

  const handleDownload = async (doc: DocumentoInst) => {
    try {
      const downloadUrl = await documentService.getDownloadUrl("institucional", doc.url);
      window.open(downloadUrl, "_blank");
    } catch (err: any) {
      alert("Error al obtener enlace seguro de descarga de Azure: " + (err.response?.data?.detail || err.message));
    }
  };

  return (
    <div className="max-w-6xl mx-auto space-y-4 p-4 animate-fade-in-up">
      <div>
        <h1 className="text-xl md:text-2xl font-bold text-foreground-950">Documentación Institucional</h1>
        <p className="text-sm text-foreground-500 mt-0.5">Gestión de reglamentos, protocolos y decretos cargados en la base de datos de Azure.</p>
      </div>

      {error && (
        <div className="p-4 bg-red-50 border border-red-200 text-red-800 text-xs rounded-xl font-medium">
          {error}
        </div>
      )}

      {/* Documents list */}
      <Card padding="none">
        <div className="overflow-x-auto">
          {loading ? (
            <div className="flex justify-center items-center py-20">
              <i className="ri-loader-4-line text-2xl text-foreground-300 animate-spin" />
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-background-200 bg-background-50/50">
                  <th className="text-left py-3 px-4 text-foreground-500 font-medium text-xs">Documento</th>
                  <th className="text-left py-3 px-4 text-foreground-500 font-medium text-xs hidden sm:table-cell">Tipo</th>
                  <th className="text-center py-3 px-4 text-foreground-500 font-medium text-xs">Estado</th>
                  <th className="text-center py-3 px-4 text-foreground-500 font-medium text-xs hidden md:table-cell">Versión</th>
                  <th className="text-right py-3 px-4 text-foreground-500 font-medium text-xs hidden md:table-cell">Almacenamiento</th>
                  <th className="text-left py-3 px-4 text-foreground-500 font-medium text-xs hidden lg:table-cell">Fecha de Carga</th>
                  <th className="text-right py-3 px-4 text-foreground-500 font-medium text-xs">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {documents.map((doc) => (
                  <tr key={doc.id} className="border-b border-background-100 hover:bg-background-50/50 transition-colors">
                    <td className="py-3 px-4">
                      <div className="flex items-center gap-2.5">
                        <div className="w-8 h-8 rounded-lg bg-background-200 flex items-center justify-center flex-shrink-0">
                          <i className="ri-file-text-line text-foreground-500 text-sm" />
                        </div>
                        <div className="min-w-0">
                          <p className="font-semibold text-foreground-900 truncate max-w-[160px] sm:max-w-xs">{doc.nombre}</p>
                        </div>
                      </div>
                    </td>
                    <td className="py-3 px-4 hidden sm:table-cell">
                      <Badge variant="neutral" size="sm">{tipoLabels[doc.tipo] || doc.tipo}</Badge>
                    </td>
                    <td className="py-3 px-4 text-center">
                      <span className="inline-flex items-center gap-1 text-xs font-semibold px-2.5 py-0.5 rounded-full bg-accent-100 text-accent-800">
                        <i className="ri-check-line text-xs" />
                        Indexado
                      </span>
                    </td>
                    <td className="py-3 px-4 text-center font-medium text-foreground-700 hidden md:table-cell">v{doc.version}</td>
                    <td className="py-3 px-4 text-right text-foreground-600 hidden md:table-cell">{doc.tamanho}</td>
                    <td className="py-3 px-4 text-xs text-foreground-500 hidden lg:table-cell whitespace-nowrap">{doc.fecha}</td>
                    <td className="py-3 px-4 text-right">
                      <div className="flex items-center justify-end gap-1">
                        <button 
                          onClick={() => handleDownload(doc)}
                          className="text-foreground-400 hover:text-accent-600 transition-colors cursor-pointer p-1" 
                          title="Descargar"
                        >
                          <i className="ri-download-line text-lg" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
                {documents.length === 0 && (
                  <tr>
                    <td colSpan={7} className="py-8 text-center text-foreground-400">
                      No hay reglamentos o protocolos indexados en la base de datos Azure.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          )}
        </div>
      </Card>

      {/* Upload zone */}
      <Card className="border-2 border-dashed border-background-300 bg-background-50/50 text-center py-8">
        <div className="flex flex-col items-center gap-3">
          <div className="w-12 h-12 rounded-xl bg-primary-100 flex items-center justify-center">
            <i className="ri-upload-cloud-2-line text-primary-500 text-xl" />
          </div>
          <div>
            <p className="text-sm font-medium text-foreground-900">
              Carga reglamentos o protocolos a Azure Storage
            </p>
            <p className="text-xs text-foreground-500 mt-1">
              Formatos aceptados: PDF, DOCX
            </p>
          </div>
          <Button variant="outline" size="sm" onClick={handleOpenUpload} className="cursor-pointer">
            Seleccionar archivo
          </Button>
        </div>
      </Card>

      {/* ── MODAL: SUBIR / CARGAR NUEVO DOCUMENTO ── */}
      {showUploadModal && createPortal(
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40" onClick={() => setShowUploadModal(false)}>
          <form onSubmit={handleUploadSubmit} className="bg-white rounded-2xl w-full max-w-md p-6 space-y-4 shadow-xl animate-scale-in" onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-between items-center pb-2 border-b border-background-150">
              <h2 className="font-bold text-foreground-950 text-base">Cargar Documento a Azure</h2>
              <button type="button" onClick={() => setShowUploadModal(false)} className="cursor-pointer text-foreground-400 hover:text-foreground-700">
                <i className="ri-close-line text-xl" />
              </button>
            </div>
            
            <div className="space-y-3">
              <div>
                <label className="block text-xs font-semibold text-foreground-600 mb-1.5">Archivo a Subir</label>
                <input
                  type="file"
                  onChange={handleFileChange}
                  accept=".pdf,.docx"
                  className="w-full text-xs text-foreground-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-xs file:font-semibold file:bg-primary-50 file:text-primary-700 hover:file:bg-primary-100 cursor-pointer"
                  required
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-foreground-600 mb-1.5">Nombre del Documento</label>
                <input
                  type="text"
                  value={newDocName}
                  onChange={(e) => setNewDocName(e.target.value)}
                  placeholder="Ej: Reglamento Interno de Convivencia"
                  className="w-full px-3 py-2 text-sm border border-background-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-primary-300"
                  required
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-foreground-600 mb-1.5">Tipo</label>
                <select
                  value={newDocType}
                  onChange={(e) => setNewDocType(e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-background-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-primary-300 cursor-pointer"
                >
                  <option value="reglamento">Reglamento (RICE)</option>
                  <option value="protocolo">Protocolo Actuación</option>
                  <option value="manual">Manual</option>
                  <option value="pei">PEI</option>
                  <option value="otro">Otro</option>
                </select>
              </div>
            </div>

            <div className="flex gap-2 pt-2">
              <Button type="button" variant="outline" onClick={() => setShowUploadModal(false)} className="flex-1 cursor-pointer">
                Cancelar
              </Button>
              <Button 
                type="submit" 
                className="flex-1 bg-primary-500 hover:bg-primary-600 text-white cursor-pointer"
                loading={uploading}
              >
                Subir a Azure
              </Button>
            </div>
          </form>
        </div>,
        document.body
      )}
    </div>
  );
}